import AddIcon from "@mui/icons-material/Add";
import { darken, lighten, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {
    DataGrid,
    GridCellModes,
    GridCellModesModel,
    GridCellParams,
    GridRowClassNameParams,
    GridRowModel,
    GridRowModes,
    GridRowModesModel,
    GridSlotProps,
    GridToolbarContainer,
    GridValidRowModel,
} from "@mui/x-data-grid";
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { observer } from "mobx-react-lite";
import { useSnackbar } from "notistack";
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from "react-router-dom";
import { io, Socket } from 'socket.io-client';
import { API } from "../../../api/api";
import { HTTP_SERVER_ORIGIN } from "../../../api/config";
import { APIRequestErrorInterface } from "../../../api/fetchApi";
import { CoworkerSchema } from "../../../api/schemas/responses/users";
import theme from "../../../assets/theme";
import useComponentSize from "../../../hooks/useComponentSize";
import i18nStore from "../../../i18n/store";
import CustomNoRowsOverlay from "../../../ui/NoRowsOverlay";
import authUserStore from "../../auth/store";
import ActiveTableEditors from "./ActiveTableEditors";
import { TableNames, TableRowLimits } from "./utils";


export const getHandlerDeleteClick = (
    projectId: any,
    rows: any,
    setRows: any,
    deleteRow: (projectId: any, id: any) => Promise<void>,
) => {
    return (id: any) => {
        const isNew: boolean = isNaN(id);
        if (isNew) {
            // Если удаление еще не сохраненной строки, то только удаляем локально
            const newRows = rows.filter((row: any) => row.id !== id);
            // @ts-ignore
            setRows(() => newRows);
            return;
        }

        deleteRow(projectId, id).then(() => {
            // @ts-ignore
            setRows((rows) => rows.filter((row: any) => row.id !== id));
        });
    }
};


export const getHandlerDuplicateClick = (
    projectId: any,
    rows: any,
    setRows: any,
    createRow: (projectId: any, row: any) => Promise<any>,
) => {
    return (row: any) => {
        createRow(projectId, row).then((r: any) => {
            let newRows = [];
            for (let i = 0; i < rows.length; i++) {
                newRows.push(rows[i]);
                if (rows[i].id === row.id) {
                    newRows.push(r);
                }
            }
            setRows(newRows);
        });
    };
};


function EditToolbar(props: GridSlotProps['toolbar']) {
    const {projectId} = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const translations = i18nStore.getTranslations;
    // @ts-ignore
    const {rows, setRows, rowModesModel, setRowModesModel, createEmptyRow, extraToolbar, createApiFunc} = props;

    const handleAddRowClick = () => {
        const newRow = createEmptyRow();
        const newUUID = newRow.id;

        createApiFunc(projectId, newRow).then((r: any) => {
            setRows((oldRows: readonly GridValidRowModel[]) => [
                r,
                ...rows,
            ] as readonly GridValidRowModel[]);

            setRowModesModel((oldModel: any) => ({
                ...rowModesModel,
                [newUUID]: {mode: GridRowModes.Edit, fieldToFocus: 'name'},
            } as any));
        }).catch((err: APIRequestErrorInterface) => {
            enqueueSnackbar(err.details.detail, {variant: "error"})
        });
    };

    return (
        <GridToolbarContainer>
            <Button startIcon={<AddIcon/>} onClick={handleAddRowClick} variant="contained">
                {translations.addRecord}
            </Button>
            {extraToolbar}
        </GridToolbarContainer>
    );
}


interface BaseTableProps {
    tableName: string;
    tableTitle: string;
    rows: any;
    setRows: (rows: any) => void;
    colDefs: any;
    columnGroupingModel?: any;
    getTableApi: any;
    createApiFunc: (projectId: string | number, row: any) => Promise<any>;
    updateApiFunc: any;
    createEmptyRow: () => object;
    extraToolbar?: any;
    getRowClassname?: (params: GridRowClassNameParams<GridValidRowModel>) => string;
}


const BaseTable = observer((props: BaseTableProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const projectId: number = +(params.projectId || 0);

    const BaseTableWrapperRef: React.MutableRefObject<any> = useRef();
    const BaseTableWrapperSize = useComponentSize(BaseTableWrapperRef);

    const [activeEditors, setActiveEditors] = React.useState<CoworkerSchema[]>([]);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

    const [socket, setSocket] = React.useState<Socket<DefaultEventsMap, DefaultEventsMap> | null>(null);
    const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});

    const handleCellClick = React.useCallback(
        (params: GridCellParams, event: React.MouseEvent) => {
            if (!params.isEditable) {
                return;
            }

            // Ignore portal
            if (
                (event.target as any).nodeType === 1 &&
                !event.currentTarget.contains(event.target as Element)
            ) {
                return;
            }

            setCellModesModel((prevModel) => {
                return {
                    // Revert the mode of the other cells from other rows
                    ...Object.keys(prevModel).reduce(
                        (acc, id) => ({
                            ...acc,
                            [id]: Object.keys(prevModel[id]).reduce(
                                (acc2, field) => ({
                                    ...acc2,
                                    [field]: {mode: GridCellModes.View},
                                }),
                                {},
                            ),
                        }),
                        {},
                    ),
                    [params.id]: {
                        // Revert the mode of other cells in the same row
                        ...Object.keys(prevModel[params.id] || {}).reduce(
                            (acc, field) => ({...acc, [field]: {mode: GridCellModes.View}}),
                            {},
                        ),
                        [params.field]: {mode: GridCellModes.Edit},
                    },
                };
            });
        },
        [],
    );

    const handleCellModesModelChange = React.useCallback(
        (newModel: GridCellModesModel) => {
            setCellModesModel(newModel);
        },
        [],
    );

    useEffect(() => {
        if (projectId) {
            props.getTableApi.apiCaller();
        }
    }, [projectId]);

    useEffect(() => {
        API.auth.getWSTicket().then(ticket => {
            const socket = io(`${HTTP_SERVER_ORIGIN}/tables`, {"auth": {"token": ticket}});
            setSocket(() => socket);
        });
    }, []);

    useEffect(() => {
        if (socket) {
            const onConnect = () => {
                socket.emit("connect_table", {
                    "table": props.tableName,
                    "project_id": projectId
                }, (data: any, val: any) => {
                    setActiveEditors((data.editors || []).filter((editor: CoworkerSchema) => editor.id !== authUserStore.getMe?.id));
                });
            }

            const onDisconnect = () => {
                console.log("disconnect server");
            }

            const onUpdateActiveEditors = (data: any) => {
                if (!Array.isArray(data) || data.length !== 2) {
                    console.log("onUpdateActiveEditors.data is not array!", data);
                    return;
                }
                const command: string = data[0];
                const args: object | object[] = data[1];
                if (command === "del_user") {
                    setActiveEditors(() => activeEditors.filter(user => user.id !== user.id));
                } else if (command === "set_users") {
                    const editors = (args as CoworkerSchema[]).filter(user => user.id !== authUserStore.getMe?.id);
                    setActiveEditors(() => [...editors]);
                }
            }
            const onChangeTable = (data: any) => {
                if (!Array.isArray(data) || data.length !== 2) {
                    console.log("onChangeTable.data is not array!", data);
                    return;
                }
                const command: string = data[0];
                if (command === "del") {
                    const deletedRowIds: number[] = data[1];
                    const deletedRowIdsSet: Set<number> = new Set(deletedRowIds);
                    props.setRows((rows: any) => rows.filter((row: any) => !deletedRowIdsSet.has(row.id)));
                } else if (command === "add") {
                    const newRow = data[1];
                    props.setRows((rows: any) => [...rows, newRow]);
                } else if (command === "upd") {
                    const updRow = data[1];
                    props.setRows((rows: any) => {
                        let newRows = [];
                        for (let i = 0; i < rows.length; i++) {
                            if (rows[i].id === updRow.id) {
                                newRows.push(updRow);
                            } else {
                                newRows.push(rows[i]);
                            }
                        }
                        return newRows;
                    });
                }
            }

            socket.on('error', (reason) => {
                console.log(reason); // prints the message associated with the error, e.g. "thou shall not pass" in the example above
            });
            socket.on('connect', onConnect);
            socket.on('disconnect', onDisconnect);
            socket.on('updateActiveEditors', onUpdateActiveEditors);
            socket.on('changeTable', onChangeTable);

            return () => {
                socket.emit("disconnect_table", {"table": props.tableName, "project_id": projectId});

                socket.disconnect();
                socket.off('connect', onConnect);
                socket.off('disconnect', onDisconnect);
                socket.off('updateActiveEditors', onUpdateActiveEditors);
                socket.off('changeTable', onChangeTable);
            };
        }
    }, [socket])

    function processRowUpdate (newRow: GridRowModel, originalRow: GridRowModel) {
        const isNew: boolean = isNaN(originalRow.id);
        if (isNew) {
            props.createApiFunc(projectId, newRow).then((r: any) => {
                let filteredRows = props.rows.filter((rows: any) => rows.id !== newRow.id);
                props.setRows([r, ...filteredRows]);
            }).catch((err: APIRequestErrorInterface) => {
                alert(err);
                enqueueSnackbar(err.details.detail);
            });
        } else {
            props.updateApiFunc(projectId, newRow);
        }
        const updatedRow = {...newRow, isNew: false};
        // @ts-ignore
        props.setRows((rows) => rows.map((row: any) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    }

    const [updatedColDefs, setUpdatedColDefs] = useState(props.colDefs);

    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});


    useEffect(() => {
        const prevTotalKeys = Object.keys(columnWidths).length;
        const proportionsSum: number = props.colDefs.reduce((acc: any, col: any) => (acc + (col.widthPart || 1)), 0);
        const fullWidth = Math.max(BaseTableWrapperSize.width - 8, 1280);
        const newColumnWidths = props.colDefs.reduce(
            (acc: any, col: any) => {
                return {
                    ...acc,
                    [col.field]: ((col.widthPart || 1) / proportionsSum * fullWidth) || col.width,
                };
            },
            {}  // init value
        );
        const newTotalKeys = Object.keys(newColumnWidths).length;
        if (props.colDefs && prevTotalKeys !== newTotalKeys && newTotalKeys > 0) {
            setColumnWidths(newColumnWidths);
        }
    }, [props.colDefs]);

    useEffect(() => {
        const updatedColDefs = props.colDefs.map((col: any) => ({
            ...col,
            width: columnWidths[col.field] || col.width,
        }));
        setUpdatedColDefs(updatedColDefs);
    }, [columnWidths]);

    return (
        <Box ref={BaseTableWrapperRef} sx={{
            minHeight: `${Math.max(400, 30*props.rows.length) + 100}px`, // Чтобы убрать .GridFiller
            maxWidth: "99%",
            pb: 1,
        }}>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1}}>
                <Typography variant="h6">
                    {props.tableTitle}&nbsp;
                    {
                        props.tableName !== TableNames.EQUIPMENTS &&
                        <Typography color={theme.palette.grey.A700} component="span">
                            (
                                { props.rows.length }
                                &nbsp;/&nbsp;
                                {
                                    props.tableName === TableNames.NODES ? TableRowLimits.NODES :
                                        props.tableName === TableNames.BRANCHES ? TableRowLimits.BRANCHES : 0
                                }
                            )
                            {/*<HelpText text={translations.tableRowLimit} />*/}
                        </Typography>
                    }
                </Typography>
                <ActiveTableEditors editors={activeEditors}/>
            </Box>
            <StyledDataGrid
                sx={{minHeight: props.rows.length > 0 ? 0 : "400px", width: "99%"}}
                getRowClassName={props.getRowClassname || (() => "")}
                rowHeight={30}
                rows={props.rows}
                columns={updatedColDefs}
                columnGroupingModel={props.columnGroupingModel}
                loading={props.getTableApi.loading}
                cellModesModel={cellModesModel}
                onCellModesModelChange={handleCellModesModelChange}
                onCellClick={handleCellClick}
                processRowUpdate={processRowUpdate}
                pageSizeOptions={[{value: -1, label: 'All'}]}
                hideFooter
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    sorting: {
                        sortModel: [{ field: 'id', sort: 'desc' }],
                    },
                }}
                slots={{
                    toolbar: EditToolbar,
                    noRowsOverlay: CustomNoRowsOverlay,
                }}
                onColumnWidthChange={(params) => {
                    setColumnWidths((prev) => ({
                        ...prev,
                        [params.colDef.field]: params.width,
                    }));
                }}

                slotProps={{
                    toolbar: {
                        rows: props.rows,
                        setRows: props.setRows,
                        createApiFunc: props.createApiFunc,
                        rowModesModel,
                        setRowModesModel,
                        createEmptyRow: props.createEmptyRow,
                        extraToolbar: props.extraToolbar,
                    } as GridSlotProps['toolbar'],
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                }}

                // onCellEditStart={(params: GridCellEditStartParams, event: MuiEvent) => {
                //     console.log("onCellEditStart");
                // }}
                // onCellEditStop={(params: GridCellEditStopParams, event: MuiEvent) => {
                //     console.log("onCellEditStop");
                // }}
                // onRowEditStart={(params: GridRowEditStartParams, event: MuiEvent) => {
                //     console.log("onRowEditStart", params, event);
                // }}
                // onRowEditStop={(params: GridRowEditStopParams, event: MuiEvent) => {
                //     console.log("onRowEditStop", params, event);
                // }}
            />
        </Box>
    );
});

export default BaseTable;


export interface TablePropsInterface {
    tableName: string;
}


const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
    backgroundColor: darken(color, coefficient),
    ...theme.applyStyles('light', {
        backgroundColor: lighten(color, coefficient),
    }),
});

const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    '& .InfoFilled': {
        ...getBackgroundColor(theme.palette.info.main, theme, 0.7),
        '&:hover': {
            ...getBackgroundColor(theme.palette.info.main, theme, 0.6),
        },
        '&.Mui-selected': {
            ...getBackgroundColor(theme.palette.info.main, theme, 0.5),
            '&:hover': {
                ...getBackgroundColor(theme.palette.info.main, theme, 0.4),
            },
        },
    },
    '& .SuccessFilled': {
        ...getBackgroundColor(theme.palette.success.main, theme, 0.7),
        '&:hover': {
            ...getBackgroundColor(theme.palette.success.main, theme, 0.6),
        },
        '&.Mui-selected': {
            ...getBackgroundColor(theme.palette.success.main, theme, 0.5),
            '&:hover': {
                ...getBackgroundColor(theme.palette.success.main, theme, 0.4),
            },
        },
    },
    '& .WarningFilled': {
        ...getBackgroundColor(theme.palette.warning.main, theme, 0.6),
        '&:hover': {
            ...getBackgroundColor(theme.palette.warning.main, theme, 0.5),
        },
        '&.Mui-selected': {
            ...getBackgroundColor(theme.palette.warning.main, theme, 0.4),
            '&:hover': {
                ...getBackgroundColor(theme.palette.warning.main, theme, 0.3),
            },
        },
    },
    '& .ErrorFilled': {
        ...getBackgroundColor(theme.palette.error.main, theme, 0.7),
        '&:hover': {
            ...getBackgroundColor(theme.palette.error.main, theme, 0.6),
        },
        '&.Mui-selected': {
            ...getBackgroundColor(theme.palette.error.main, theme, 0.5),
            '&:hover': {
                ...getBackgroundColor(theme.palette.error.main, theme, 0.4),
            },
        },
    },
}));
